/**
 * Settings → Appearance (design 2026-10-08: Oppih "Civic selected", X1QwI "Graphite applied", vcwY7 "custom theme editor";
 * components Theme Tile b9BiUf and Theme Tile / Create aHhFI).
 * Theme card: the seven themes in rows of four plus "Create a custom theme", and "Match my device" (Graphite while the
 * device is dark). Display card: "Higher contrast text" and "Reduce motion". Every change shows at once and is saved per
 * user (theme/runtime.ts → PUT /api/dashboard/settings/appearance).
 */
import { useEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import { ArrowLeft, ArrowRight, Bell, Calendar, Check, CircleCheck, LayoutGrid, Plus, ScrollText, TrendingUp, TriangleAlert, Users } from "lucide-react";
import { Switch } from "../components/shared/ui";
import { ListRow, RowMain, RowSub, RowTitle, RowTrail } from "../components/shared/List";
import { toastStore } from "../stores/toastStore";
import { saveAppearance, useAppearance } from "../theme/runtime";
import {
  CUSTOM_KEYS, PRESET_IDS, SAMPLE_CUSTOM, THEME_LABEL, colorsOf, deriveCustomTheme, isHex6, presetVars, readability,
  type CustomKey, type CustomTheme, type PresetId, type ThemeId, type Vars,
} from "../theme/themes";

/** A theme's variables as inline custom properties: previews show their theme whatever the page's theme is. */
const varsStyle = (v: Vars) => Object.fromEntries(Object.entries(v).map(([k, x]) => [`--${k}`, x])) as CSSProperties;
const failed = () => toastStore.getState().show({ message: "Couldn't save that. Try again." });

// ------------------------------------------------------------------ Theme Tile (b9BiUf)
const BARS: [number, 1 | 2 | 3][] = [[14, 1], [22, 1], [12, 2], [26, 1], [18, 3], [24, 1], [10, 2], [20, 1]];

function TilePreview({ vars }: { vars: Vars }) {
  return (
    <div className="ap-tile__preview" style={varsStyle(vars)} aria-hidden>
      <div className="ap-tile__rail">
        <i className="ap-tile__logo" /><i className="ap-tile__gap" />
        <i className="ap-tile__nav is-active" /><i className="ap-tile__nav" /><i className="ap-tile__nav is-short" /><i className="ap-tile__nav" />
      </div>
      <div className="ap-tile__main">
        <div className="ap-tile__top"><i className="ap-tile__title" /><i className="ap-tile__btn" /></div>
        <div className="ap-tile__cards">
          {(["positive", "chart-2"] as const).map((chip) => (
            <div className="ap-tile__stat" key={chip}><i className="ap-tile__lbl" /><span><i className="ap-tile__value" /><i className="ap-tile__chip" style={{ background: `var(--${chip})` }} /></span></div>
          ))}
        </div>
        <div className="ap-tile__chart">{BARS.map(([h, c], i) => <i key={i} style={{ height: h, background: `var(--chart-${c})` }} />)}</div>
      </div>
    </div>
  );
}

function ThemeTile({ id, name, vars, selected, onSelect }: { id: ThemeId; name: string; vars: Vars; selected: boolean; onSelect: () => void }) {
  return (
    <button type="button" className="ap-tile" role="radio" aria-checked={selected} data-theme-tile={id} onClick={onSelect}>
      <TilePreview vars={vars} />
      <span className="ap-tile__label"><span className="ap-tile__name">{name}</span><span className="ap-radio" aria-hidden><i /></span></span>
    </button>
  );
}

// ------------------------------------------------------------------ Appearance cards
function Row({ title, text, on, onChange }: { title: string; text: string; on: boolean; onChange: (v: boolean) => void }) {
  return (
    <ListRow as="div" className="set-row ap-row">
      <RowMain className="ap-row__text"><RowTitle as="b">{title}</RowTitle><RowSub wrap>{text}</RowSub></RowMain>
      <RowTrail><Switch on={on} onChange={onChange} label={title} /></RowTrail>
    </ListRow>
  );
}

export function AppearanceCards({ onCreate }: { onCreate: () => void }) {
  const a = useAppearance();
  const save = (patch: Parameters<typeof saveAppearance>[0]) => void saveAppearance(patch).then((ok) => { if (!ok) failed(); });
  const presetTiles = useMemo(() => PRESET_IDS.map((id) => ({ id, vars: presetVars(id) })), []);
  const customVars = useMemo(() => (a.custom ? deriveCustomTheme(a.custom) : null), [a.custom]);
  return (
    <>
      <section className="set-card ap-card" id="appearance" data-section="appearance" aria-labelledby="appearance-title">
        <h2 id="appearance-title" className="ap-card__title">Theme</h2>
        <div className="ap-tiles" role="radiogroup" aria-labelledby="appearance-title">
          {presetTiles.map((t) => <ThemeTile key={t.id} id={t.id} name={THEME_LABEL[t.id]} vars={t.vars} selected={a.theme === t.id} onSelect={() => save({ theme: t.id })} />)}
          {a.custom && customVars && (
            <ThemeTile id="custom" name={a.custom.name || THEME_LABEL.custom} vars={customVars} selected={a.theme === "custom"} onSelect={() => save({ theme: "custom" })} />
          )}
          <button type="button" className="ap-create" onClick={onCreate}>
            <span className="ap-create__icon"><Plus size={16} aria-hidden /></span>
            <b>Create a custom theme</b>
          </button>
        </div>
        <Row title="Match my device" text="Use Graphite when your device is in dark mode." on={a.matchDevice} onChange={(v) => save({ matchDevice: v })} />
      </section>
      <section className="set-card ap-card ap-card--display" id="appearance-display" data-section="appearance" aria-labelledby="appearance-display-title">
        <h2 id="appearance-display-title" className="ap-card__title">Display</h2>
        <Row title="Higher contrast text" text="Darkens secondary text and borders in any theme" on={a.highContrast} onChange={(v) => save({ highContrast: v })} />
        <Row title="Reduce motion" text="Turns off chart animations and sliding panels." on={a.reduceMotion} onChange={(v) => save({ reduceMotion: v })} />
      </section>
    </>
  );
}

// ------------------------------------------------------------------ Custom theme editor (vcwY7)
const COLOR_ROWS: { key: CustomKey; title: string; text: string }[] = [
  { key: "background", title: "Background", text: "Behind everything" },
  { key: "cards", title: "Cards", text: "Panels, menus and tables" },
  { key: "text", title: "Text", text: "Headings and body text" },
  { key: "accent", title: "Accent", text: "Active items and the first chart series" },
  { key: "chart2", title: "Chart 2", text: "Second series, “against” forecasts" },
  { key: "chart3", title: "Chart 3", text: "Third series and AI estimates" },
];
const PRESET_ROWS: PresetId[][] = [["civic", "modern", "graphite", "lakefront"], ["prairie", "harbor", "rose"]];

function ColorRow({ row, value, onChange }: { row: (typeof COLOR_ROWS)[number]; value: string; onChange: (hex: string) => void }) {
  const [text, setText] = useState(value);
  useEffect(() => setText(value), [value]);
  const commit = (raw: string) => {
    const hex = `#${raw.replace(/^#/, "").trim()}`.toUpperCase();
    if (isHex6(hex)) onChange(hex);
  };
  return (
    <div className="ap-color">
      <label className="ap-color__swatch" style={{ background: value }}>
        <input type="color" value={value.toLowerCase()} onChange={(e) => onChange(e.target.value.toUpperCase())} aria-label={`${row.title} color`} />
      </label>
      <div className="ap-row__text"><b>{row.title}</b><span>{row.text}</span></div>
      <input className="ap-color__hex" value={text} spellCheck={false} maxLength={7} aria-label={`${row.title} hex`}
        onChange={(e) => { setText(e.target.value); commit(e.target.value); }} onBlur={() => setText(value)} />
    </div>
  );
}

const MONTHS: [number, number, number][] = [[114, 42, 15], [141, 30, 11], [91, 57, 19], [156, 23, 8], [125, 38, 23], [171, 19, 11]];
const PREVIEW_NAV = [
  { label: "Overview", Icon: LayoutGrid, active: true }, { label: "Meetings", Icon: Calendar }, { label: "Legislation", Icon: ScrollText }, { label: "People", Icon: Users },
];

/** The live preview: real dashboard components (buttons, chips, cards, nav items) in the theme being made. */
function CustomPreview({ vars }: { vars: Vars }) {
  return (
    <div className="ap-preview" style={varsStyle(vars)} aria-hidden inert>
      <div className="ap-preview__rail">
        <i className="ap-preview__logo" />
        {PREVIEW_NAV.map(({ label, Icon, active }) => (
          <span key={label} className={`ap-preview__nav${active ? " is-active" : ""}`}><Icon size={13} />{label}</span>
        ))}
      </div>
      <div className="ap-preview__main">
        <div className="ap-preview__top">
          <div><h3 className="ap-preview__title">Good morning, Alex</h3><p>Two things you follow moved this week</p></div>
          <button className="btn btn--primary btn--lg" tabIndex={-1}><Bell size={14} />Follow</button>
        </div>
        <div className="ap-preview__stats">
          <div className="ap-preview__stat"><span>Next vote</span><b>Oct 14</b>
            <span className="forecast-chip forecast-chip--likely"><TrendingUp size={12} /><span className="forecast-chip__text">Likely · 78%</span></span></div>
          <div className="ap-preview__stat"><span>Bills you follow</span><b>11</b><span className="chip chip--positive">2 passed</span></div>
        </div>
        <div className="ap-preview__chart">
          <div className="ap-preview__chart-head"><b>Votes by month</b><span className="link-arrow">Open<ArrowRight size={14} /></span></div>
          <div className="ap-preview__legend">{["Yes", "No", "Absent"].map((l, i) => <span key={l}><i style={{ background: `var(--chart-${i + 1})` }} />{l}</span>)}</div>
          <div className="ap-preview__bars">
            {MONTHS.map((m, i) => <span key={i}>{m.map((h, j) => <i key={j} style={{ height: h, background: `var(--chart-${j + 1})` }} />)}</span>)}
          </div>
        </div>
      </div>
    </div>
  );
}

export function CustomThemeEditor({ onDone }: { onDone: () => void }) {
  const a = useAppearance();
  const [t, setT] = useState<CustomTheme>(() => a.custom ?? SAMPLE_CUSTOM);
  const [saving, setSaving] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  const vars = useMemo(() => deriveCustomTheme(t), [t]);
  const checks = useMemo(() => readability(t), [t]);
  useEffect(() => { root.current?.scrollIntoView({ block: "start" }); }, []);
  const set = (k: CustomKey) => (hex: string) => setT((x) => ({ ...x, [k]: hex }));
  const save = async () => {
    setSaving(true);
    const ok = await saveAppearance({ theme: "custom", custom: { ...t, name: t.name.trim() } });
    setSaving(false);
    if (ok) onDone(); else failed();
  };
  return (
    <div className="ap-editor" ref={root} id="appearance" data-section="appearance">
      <button type="button" className="ap-back" onClick={onDone}><ArrowLeft size={14} />Appearance</button>
      <section className="ap-custom" aria-labelledby="custom-theme-title">
        <h2 id="custom-theme-title" className="ap-custom__head">Create a custom theme</h2>
        <div className="ap-custom__cols">
          <div className="ap-custom__form">
            <label className="ap-field"><span className="ap-label">Name</span>
              <input className="ap-input" value={t.name} maxLength={60} onChange={(e) => setT((x) => ({ ...x, name: e.target.value }))} /></label>
            <div className="ap-field"><span className="ap-label" id="start-from">Start from</span>
              <div className="ap-presets" role="radiogroup" aria-labelledby="start-from">
                {PRESET_ROWS.map((row, i) => (
                  <div className="ap-presets__row" key={i}>
                    {row.map((id) => {
                      const c = colorsOf(id);
                      return (
                        <button key={id} type="button" role="radio" aria-checked={t.base === id} className="ap-preset" onClick={() => setT((x) => ({ ...x, ...colorsOf(id), base: id }))}>
                          <span className="ap-preset__dot" style={{ background: c.background }}><i style={{ background: c.accent }} /></span>{THEME_LABEL[id]}
                        </button>
                      );
                    })}
                  </div>
                ))}
              </div>
            </div>
            <div className="ap-colors"><span className="ap-label">Colors</span>
              {COLOR_ROWS.map((r) => <ColorRow key={r.key} row={r} value={t[r.key]} onChange={set(r.key)} />)}
            </div>
            <div className="ap-readability" aria-live="polite"><span className="ap-label">Readability</span>
              {checks.map((c) => (
                <div key={c.id} className={`ap-check${c.ok ? "" : " is-low"}`}>
                  {c.ok ? <CircleCheck size={14} aria-hidden /> : <TriangleAlert size={14} aria-hidden />}
                  <b>{c.label}</b><span>{c.text}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="ap-custom__preview"><CustomPreview vars={vars} /></div>
        </div>
        <footer className="ap-custom__foot">
          <button type="button" className="btn btn--secondary btn--lg" onClick={onDone}>Cancel</button>
          <button type="button" className="btn btn--primary btn--lg" disabled={saving || !CUSTOM_KEYS.every((k) => isHex6(t[k]))} onClick={() => void save()}><Check size={14} />Save and use theme</button>
        </footer>
      </section>
    </div>
  );
}
